import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smile } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { useOptionalCRM } from '../../utils/CRMContext';
import { db } from '../../utils/firebase';
import { compactRelativeTime } from '../../utils/notificationInbox';
import {
  buildCommentReactionEventPath,
  lookupCommentReactionDestination,
  resolveReactionProjectLabel,
  subscribeCommentReactionEvents,
  type CommentReactionEvent,
} from '../../utils/commentReactionEvents';
import {
  mergeCommentReactionFeeds,
  subscribeLiveAuthorCommentReactions,
} from '../../utils/commentReactionFeed';
import './NotificationRow.css';

const PULSE_MS = 1200;

interface CommentReactionPulseProps {
  panelOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const CommentReactionPulse = forwardRef<HTMLDivElement, CommentReactionPulseProps>(
  function CommentReactionPulse({ panelOpen, onOpen, onClose }, ref) {
  const { user } = useAuth();
  const crm = useOptionalCRM();
  const navigate = useNavigate();
  const [logEvents, setLogEvents] = useState<CommentReactionEvent[]>([]);
  const [liveEvents, setLiveEvents] = useState<CommentReactionEvent[]>([]);
  const [pulseEmoji, setPulseEmoji] = useState<string | null>(null);
  const hydratedRef = useRef(false);
  const lastSeenIdRef = useRef<string | null>(null);
  const pulseTimerRef = useRef<number | null>(null);
  const openingRef = useRef(false);

  const events = mergeCommentReactionFeeds(logEvents, liveEvents);

  useEffect(() => {
    hydratedRef.current = false;
    lastSeenIdRef.current = null;
    setLogEvents([]);
    setLiveEvents([]);
    setPulseEmoji(null);
    if (!user?.id) return undefined;
    const unsubLog = subscribeCommentReactionEvents(db, user.id, (next) => {
      setLogEvents(next);
      if (!hydratedRef.current) {
        lastSeenIdRef.current = next[0]?.id ?? null;
        hydratedRef.current = true;
        return;
      }
      const newest = next[0];
      if (!newest || newest.id === lastSeenIdRef.current) return;
      lastSeenIdRef.current = newest.id;
      if (pulseTimerRef.current != null) window.clearTimeout(pulseTimerRef.current);
      setPulseEmoji(newest.emoji);
      pulseTimerRef.current = window.setTimeout(() => {
        setPulseEmoji(null);
        pulseTimerRef.current = null;
      }, PULSE_MS);
    });
    const unsubLive = subscribeLiveAuthorCommentReactions(db, user.id, setLiveEvents);
    return () => {
      unsubLog();
      unsubLive();
    };
  }, [user?.id]);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current != null) window.clearTimeout(pulseTimerRef.current);
    };
  }, []);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen();
  };

  const handleRowClick = (event: CommentReactionEvent) => {
    if (openingRef.current) return;
    openingRef.current = true;
    const known = buildCommentReactionEventPath(event, user?.role);
    const open = known
      ? Promise.resolve(known)
      : lookupCommentReactionDestination(db, event, user?.role);
    void open
      .then((path) => {
        if (!path) return;
        onClose();
        navigate(path);
      })
      .finally(() => {
        openingRef.current = false;
      });
  };

  const actorName = (actorId: string) =>
    crm?.crmUsers?.find((u) => u.uid === actorId)?.displayName || 'Someone';
  const reactionTime = (date: Date | undefined) => {
    if (!date || Number.isNaN(date.getTime()) || date.getTime() < 1_000_000_000_000) return '';
    return compactRelativeTime(date);
  };

  return (
    <div className="notification-reaction-wrap" ref={ref}>
      <button
        type="button"
        className={`notification-reaction${pulseEmoji ? ' is-pulse' : ''}`}
        aria-label="Reactions on your comments"
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
        onMouseEnter={onOpen}
        onFocus={onOpen}
        onClick={handleButtonClick}
      >
        {pulseEmoji ? (
          <span className="notification-reaction-emoji" aria-hidden>
            {pulseEmoji}
          </span>
        ) : (
          <Smile size={20} strokeWidth={2} aria-hidden />
        )}
      </button>
      {panelOpen ? (
        <div
          className="notification-reaction-panel"
          role="dialog"
          aria-label="Reactions"
        >
          <div className="notification-header">
            <h3>Reactions</h3>
          </div>
          <div className="notification-content">
            {events.length === 0 ? (
              <div className="notification-empty">
                <Smile size={18} />
                <p>No reactions yet</p>
              </div>
            ) : (
              <div className="notification-section">
                {events.map((event) => {
                  const label = resolveReactionProjectLabel({
                    storedLabel: event.projectLabel,
                    projectId: event.projectId,
                    dealId: event.dealId,
                    deals: crm?.enhancedDeals,
                    projects: crm?.timberProjects,
                  });
                  const path = buildCommentReactionEventPath(event, user?.role);
                  const canOpen = !!path || !!event.commentId;
                  const time = reactionTime(event.createdAt);
                  const typeGroup = event.documentId ? 'files' : 'comments';
                  const typeLabel = label
                    || (event.documentId ? 'PDF' : event.leadId ? 'Note' : event.opsTaskId ? 'Ops' : 'Comment');
                  const name = actorName(event.actorId);
                  const snippet = event.commentPreview || '';
                  return (
                    <button
                      key={event.id}
                      type="button"
                      className={`notif-row${canOpen ? ' is-clickable' : ''}`}
                      disabled={!canOpen}
                      aria-label={`${event.emoji} ${name}${snippet ? `. ${snippet}` : ''}`}
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        handleRowClick(event);
                      }}
                      onClick={() => handleRowClick(event)}
                    >
                      <span className="notif-row-dot notification-reaction-row-emoji" aria-hidden>
                        {event.emoji}
                      </span>
                      <span className="notif-row-copy">
                        <span className="notif-row-title">{name}</span>
                        {snippet ? (
                          <span className="notif-row-message"> — {snippet}</span>
                        ) : null}
                      </span>
                      <span className={`notif-row-type notif-type-${typeGroup}`}>
                        {typeLabel}
                      </span>
                      <span className="notif-row-time">{time}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
});

CommentReactionPulse.displayName = 'CommentReactionPulse';

export default CommentReactionPulse;
